import {
  CurrencyCircleDollar, ChartLineUp, Sparkle, Globe, Users,
  Lightning, Code, Buildings, Rocket, Compass,
} from '@phosphor-icons/react';
import { brand } from '@lunarcrush/ds/tokens';
import type { UseCase } from './cms';

export type MethodKey = 'discover' | 'claude' | 'claudeCode' | 'api';

export const METHOD_TABS: { key: MethodKey; label: string; icon: React.FC<{ size?: number | string; color?: string }> }[] = [
  { key: 'discover', label: 'DISCOVER', icon: Compass },
  { key: 'claude', label: 'CLAUDE', icon: Sparkle },
  { key: 'claudeCode', label: 'CLAUDE CODE', icon: Code },
  { key: 'api', label: 'API', icon: Rocket },
];

export type CategoryKey =
  | 'crypto'
  | 'stocks'
  | 'nfts'
  | 'social'
  | 'creators'
  | 'sports'
  | 'technology'
  | 'finance';

export const CATEGORIES: { key: CategoryKey; label: string; icon: React.FC<{ size?: number | string; color?: string }>; color: string }[] = [
  { key: 'crypto', label: 'Crypto', icon: CurrencyCircleDollar, color: brand.purple },
  { key: 'stocks', label: 'Stocks', icon: ChartLineUp, color: brand.green },
  { key: 'nfts', label: 'NFTs', icon: Sparkle, color: brand.amber },
  { key: 'social', label: 'Social Analytics', icon: Globe, color: brand.cyan },
  { key: 'creators', label: 'Creators', icon: Users, color: brand.purpleLight },
  { key: 'sports', label: 'Sports', icon: Lightning, color: brand.green },
  { key: 'technology', label: 'Technology', icon: Code, color: brand.cyan },
  { key: 'finance', label: 'Finance', icon: Buildings, color: brand.amber },
];

export function useCaseSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[™®©]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function getUseCaseBySlug(cases: UseCase[], slug: string): UseCase | undefined {
  return cases.find((uc) => useCaseSlug(uc.title) === slug);
}

export function getRelatedUseCases(cases: UseCase[], current: UseCase, limit = 6): UseCase[] {
  return cases
    .filter((uc) => uc.title !== current.title)
    .filter((uc) => {
      const sameAsset = uc.assetClass.toLowerCase() === current.assetClass.toLowerCase();
      const sameDataType = uc.dataType.toLowerCase() === current.dataType.toLowerCase();
      const sameWorkflow = uc.workflow.toLowerCase() === current.workflow.toLowerCase();
      return sameAsset || sameDataType || sameWorkflow;
    })
    .slice(0, limit);
}

export function matchesCategory(uc: UseCase, category: CategoryKey): boolean {
  const ac = uc.assetClass.toLowerCase();
  const dt = uc.dataType.toLowerCase();

  switch (category) {
    case 'crypto':
      return ac.includes('crypto');
    case 'stocks':
      return ac.includes('stocks');
    case 'nfts':
      return ac === 'nfts';
    case 'social':
      if (['geopolitics', 'travel', 'events'].includes(ac)) return true;
      if (ac !== 'any') return false;
      if (dt.includes('creator')) return false;
      if (dt === 'api integration') return false;
      return true;
    case 'creators':
      return dt.includes('creator');
    case 'sports':
      return ac === 'sports';
    case 'technology':
      return ac === 'technology';
    case 'finance':
      return ac === 'finance';
    default:
      return false;
  }
}
